/**
 * GIL-006: sanitize API error bodies before showing them in the UI.
 * Drops stack traces, SQL/provider text, and internal host fingerprints.
 */
const SENSITIVE =
  /stack\s*trace|at\s+\w+(\.\w+)+\(|[A-Za-z]:\\|\\\\[a-zA-Z]|SqlException|Microsoft\.Data|System\.|Server\s*=|Data\s*Source|amazonaws\.com|ec2-|internal\.|localhost:\d+|127\.0\.0\.1|node\.js|ASP\.NET|Kestrel|\.NET\s*\d/i;

export function sanitizeClientErrorMessage(message, fallback = "An unexpected error occurred. Please try again or contact support.") {
  if (message == null) return fallback;
  const text = String(message).trim();
  if (!text) return fallback;
  const firstLine = text.split("\n")[0].trim();
  if (!firstLine || SENSITIVE.test(firstLine) || firstLine.length > 400) {
    return fallback;
  }
  return firstLine;
}

/**
 * User-facing message for failed API calls (pages + global connectivity).
 */
export function getApiErrorMessage(error, fallback = "Unable to load data from the server.") {
  if (!error) return fallback;
  if (error.isSessionExpired) {
    return "Your session has expired. Please sign in again.";
  }
  if (error.isUnauthorized) {
    return "You are not signed in or your session has expired.";
  }
  if (error.isForbidden) {
    return "You do not have permission to perform this action.";
  }
  if (error.isTimeout) {
    return "The server did not respond in time. Please try again or contact support if this continues.";
  }
  if (error.code === "ERR_NETWORK" || String(error.message || "").includes("Network Error")) {
    return "Cannot reach the CSRP server. Verify the API is running and your network connection.";
  }
  const status = error.response?.status;
  if (status === 504) {
    return "The server timed out. Please try again in a moment.";
  }
  if (status === 502 || status === 503) {
    return "The server is temporarily unavailable. Please try again shortly.";
  }
  if (status === 500) {
    const bodyMsg =
      error.response?.data?.message ||
      error.response?.data?.Message ||
      error.response?.data?.title;
    return sanitizeClientErrorMessage(
      typeof bodyMsg === "string" ? bodyMsg : null,
      "The server encountered an error. Please try again or contact support."
    );
  }
  if (status === 404) {
    return "The requested resource was not found.";
  }
  const msg = error.response?.data?.message || error.response?.data?.Message;
  if (typeof msg === "string" && msg.trim()) {
    return sanitizeClientErrorMessage(msg.trim(), fallback);
  }
  if (typeof error.message === "string" && error.message.trim()) {
    return sanitizeClientErrorMessage(error.message.trim(), fallback);
  }
  return fallback;
}
